import React, { Component } from 'react';
import { FieldGroup, FieldControl, FormBuilder, Validators } from 'react-reactive-form';
import PropTypes from 'proptypes';
import { Auth, API, Storage } from 'aws-amplify';
import { readableResponseMessage, scroller } from 'ux-helpers';
import DatePicker from 'react-datepicker';
import Modal from 'react-modal';
import moment from 'moment';
import PassportPreviewModal from './previewModals/PassportPreviewModal';

import '../documentsAccordion/DocumentsAccordion.scss';

export default class Passport extends Component {
  state = {
    passportDoc: this.props.passport ? this.props.passport : {},
    instructions: false,
    issueDate:
      this.props.passport && this.props.passport.issueDate
        ? moment(this.props.passport.issueDate)
        : moment(),
    expirationDate:
      this.props.passport && this.props.passport.expirationDate
        ? moment(this.props.passport.expirationDate)
        : moment(),
  };

  componentDidMount() {
    const issueDateInput = document.getElementById('issueDate');

    if (issueDateInput !== null) {
      issueDateInput.readOnly = true;
    }

    const expirationDateInput = document.getElementById('expirationDate');

    if (expirationDateInput !== null) {
      expirationDateInput.readOnly = true;
    }

    let uploadField;

    if (document.getElementById('initial-pp-upload-field') !== null) {
      uploadField = document.getElementById('initial-pp-upload-field');
    } else {
      uploadField = document.getElementById('update-pp-upload-field');
    }

    if (
      Object.keys(this.state.passportDoc).length &&
      this.state.passportDoc.status !== 'Not Required Based on Citizenship'
    ) {
      uploadField.onchange = () => {
        if (uploadField.files.length && uploadField.files[0].size > 4000000) {
          this.setState({
            successOrErrorMessage: false,
            message: 'File size exceeds 4mb. Please choose a smaller file.',
          });
          uploadField.value = '';
        } else {
          this.setState({
            message: '',
          });
        }
      };
    }
  }

  uploadDocument = document => {
    const fileName = document.name;
    const fileType = document.type;
    Storage.put(fileName, document, {
      level: 'protected',
      contentType: fileType,
    }).catch(error => {
      this.setState({
        message: 'Whoops! Looks like we had a problem uploading your file.',
      });
    });
  };

  onImageSelect = e => {
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      reader.addEventListener(
        'load',
        () =>
          this.setState({
            passport: reader.result,
          }),
        false
      );
      reader.readAsDataURL(e.target.files[0]);
      this.passportForm.controls.travelDocument.setValue(e.target.files[0]);
      this.setState({
        travelDocumentFileName: e.target.files[0].name,
      });
    }
  };

  returnFileNameAndType = () => {
    const url = this.state.passportDoc.travelDocumentURL;
    const fileName = url.substring(url.lastIndexOf('/') + 1);
    const extension = fileName.substring(fileName.lastIndexOf('.') + 1);
    return {
      key: url,
      type: extension,
    };
  };

  openModal = modalName => {
    let fileAttributes = this.returnFileNameAndType();
    Storage.get(fileAttributes.key, { level: 'protected' })
      .then(result => {
        if (
          fileAttributes.type === 'pdf' ||
          fileAttributes.type === 'doc' ||
          fileAttributes.type === 'docx'
        ) {
          window.open(result);
        } else {
          this.setState({
            previewUrl: result,
            activeModal: modalName,
          });
        }
      })
      .catch(() => {});
  };

  closeModal = () => {
    this.setState({
      activeModal: null,
    });
  };

  handleSubmit = e => {
    e.preventDefault();
    if (this.passportForm.status === 'VALID') {
      this.setState({ loadingBtn: true });
      this.passportForm.handleSubmit();
      Auth.currentSession().then(user => {
        const options = {
          headers: {
            Authorization: user.idToken.jwtToken,
            Token: user.accessToken.jwtToken,
            'Content-Type': 'application/json',
          },
          body: this.passportForm.value,
        };
        options.body.userId = this.props.userId;
        options.body.userName = user.idToken.payload.nickname;
        options.body.travelDocument = this.state.passport;
        options.body.fileName = this.state.travelDocumentFileName;
        this.uploadDocument(this.passportForm.controls.travelDocument.value);
        API.post('give-api', `/documents/${this.props.tripId}/passport/`, options)
          .then(response => {
            this.setState({
              successOrErrorMessage: true,
              loadingBtn: false,
              message: response.Message,
              passportDoc: {
                passport: response.Data.Passport__c,
                issueDate: response.Data.Issue_Date__c,
                expirationDate: response.Data.Expiration_Date__c,
                travelDocumentURL: response.Data.travelDocumentUrl
                  ? response.Data.travelDocumentUrl
                  : this.state.passportDoc.travelDocumentURL,
              },
            });
            this.props.setDocumentsAccordionState('passport');
            this.passportForm.handleSubmit();
            this.setState({ passport: '' });
            this.props.deactiveActiveAccordion('passport-widget');
            scroller('passport');
            setTimeout(() => {
              this.setState({ message: '' });
            }, 3000);
          })
          .catch(error => {
            this.setState({
              successOrErrorMessage: false,
              loadingBtn: false,
              message: readableResponseMessage(error),
            });
            this.passportForm.handleSubmit();
            scroller('passport');
          });
      });
    } else {
      this.passportForm.handleSubmit();
    }
  };

  handleIssueDateChange = date => {
    const formattedDate = moment(date).format();
    this.passportForm.controls.issueDate.setValue(formattedDate);
    this.setState({
      issueDate: date,
    });
  };

  handleExpirationDateChange = date => {
    const formattedDate = moment(date).format();
    this.passportForm.controls.expirationDate.setValue(formattedDate);
    this.setState({
      expirationDate: date,
    });
  };

  validateExpirationDate = control => {
    let expirationDate = moment(control.value);

    let minimumExpDate = moment(this.props.endDate, 'MMMM DD, YYYY').add(6, 'month');

    if (moment(expirationDate).isBefore(minimumExpDate)) {
      if (typeof control.value === 'string') {
        this.setState({
          successOrErrorMessage: false,
          message: `Your passport cannot expire before ${moment(minimumExpDate).format(
            'MMMM Do, YYYY'
          )}. Please choose a different date.`,
        });
      }
      return { invalidExpirationDate: true };
    } else {
      if (typeof control.value === 'string') {
        this.setState({
          message: '',
        });
      }
      return null;
    }
  };

  passportForm = FormBuilder.group({
    passport: [
      this.state.passportDoc && this.state.passportDoc.passport
        ? this.state.passportDoc.passport
        : '',
      [Validators.required, Validators.maxLength(16)],
    ],
    issueDate: [this.state.issueDate ? this.state.issueDate : '', [Validators.required]],
    expirationDate: [
      this.state.expirationDate ? this.state.expirationDate : '',
      [Validators.required, this.validateExpirationDate],
    ],
    travelDocument: [
      this.state.passportDoc && this.state.passportDoc.travelDocumentURL
        ? this.state.passportDoc.travelDocumentURL
        : '',
      Validators.required,
    ],
  });

  render() {
    const { mailingCountry } = this.props;
    return (
      <div>
        {this.state.passportDoc.reasonDeclined ? (
          <div className="col-md-12 warning-text-container">
            <p className="warning-color">{this.state.passportDoc.reasonDeclined}</p>
          </div>
        ) : (
          ''
        )}
        <p>
          <span className="round-icon small">1</span> Please input your passport information below.
          <br />
          <br />
          <strong>
            NOTE: Your passport cannot expire before{' '}
            {moment(this.props.endDate, 'MMMM DD, YYYY')
              .add(6, 'month')
              .format('MMMM Do, YYYY')}
            .
          </strong>
          <br />
        </p>
        <button
          className="btn-plain-text"
          onClick={() => {
            this.setState(({ instructions }) => ({
              instructions: !instructions,
            }));
            setTimeout(() => {
              this.props.setHeightState('passport-widget');
            }, 0);
          }}
        >
          {!this.state.instructions ? "Don't have a passport or expiring too soon?" : 'Close'}
        </button>
        <div className={`form-widget ${!this.state.instructions ? 'up' : 'down'}`}>
          {this.props.mailingCountry === 'Australia' ? (
            <p>
              <a
                href="https://www.passports.gov.au/passports-explained/adult-quick-guide-applying-passport"
                target="_blank"
                rel="noopener noreferrer"
              >
                Review and complete the application process to apply for your passport
              </a>
              . You must provide proof of Australian citizenship, identification, and two identical
              35-40mm wide and 45-50mm photos taken within last the 6 months. For information
              regarding photos click{' '}
              <a
                href="https://www.passports.gov.au/passports-explained/how-apply/passport-photo-guidelines"
                target="_blank"
                rel="noopener noreferrer"
              >
                here.
              </a>
            </p>
          ) : (
            ''
          )}
          {mailingCountry === 'United Kingdom' ? (
            <p>
              You can find all the resources you need regarding your passport online{' '}
              <a
                href="https://www.gov.uk/browse/abroad/passports"
                target="_blank"
                rel="noopener noreferrer"
              >
                here
              </a>
              . For your first passport, the process is a bit different. To{' '}
              <a
                href="https://www.gov.uk/apply-first-adult-passport"
                target="_blank"
                rel="noopener noreferrer"
              >
                apply for your passport
              </a>
              , you must provide proof of citizenship, proof of identification and birth
              certificate. You also need to provide two identical 35-40mm wide and 45-50mm high
              passport photographs that have been taken within the last 6 months. Follow the
              guidelines online listed for more{' '}
              <a
                href="https://www.gov.uk/browse/abroad/passports"
                target="_blank"
                rel="noopener noreferrer"
              >
                requirements
              </a>{' '}
              to obtain your passport.
            </p>
          ) : (
            ''
          )}

          {mailingCountry === 'United States' ? (
            <p>
              {' '}
              Learn{' '}
              <a
                href="https://travel.state.gov/content/travel/en/passports/apply-renew-passport/how-to-apply.html"
                target="_blank"
                rel="noopener noreferrer"
              >
                how to apply for a passport online here
              </a>{' '}
              – this website will walk you through the process step-by-step. For more information or
              general options about applying for your passport,{' '}
              <a
                href="https://travel.state.gov/content/travel/en/passports.html"
                target="_blank"
                rel="noopener noreferrer"
              >
                click here
              </a>
              .<br />
              <br />
              To apply for your passport, you must provide proof of U.S. citizenship and proof of
              identification. You will also need to provide two identical 2-inch by 2-inch passport
              photographs, which you can get at locations such as Walgreens or FedEx for $10.00
            </p>
          ) : (
            ''
          )}

          <p>
            To avoid fees we recommend ordering your passport ASAP, as it can take four to six weeks
            to arrive.{' '}
            <a
              href="https://www.givevolunteers.org/prepping-trip/travel-details/#passport"
              target="_blank"
              rel="noopener noreferrer"
            >
              Click here
            </a>{' '}
            for more information.
          </p>
        </div>
        <FieldGroup
          control={this.passportForm}
          render={({ value, submitted }) => (
            <form
              name="Passport"
              id="passport-form"
              onSubmit={e => this.handleSubmit(e, value)}
              autoComplete="off"
            >
              <div>
                <FieldControl
                  name="passport"
                  render={({ handler, touched, hasError, pristine }) => (
                    <div className="inputContainer">
                      <input id="passport" className="form-control" type="text" {...handler()} />
                      <label
                        htmlFor="passport"
                        className={`label ${
                          pristine && !this.passportForm.controls.passport.value ? '' : 'notEmpty'
                        }`}
                      >
                        Passport Number
                        <span className="error">
                          {(touched || submitted) && hasError('required') && ' - Required'}
                          {hasError('maxLength') && ' - Exceeds Max Character Limit '}
                        </span>
                      </label>
                    </div>
                  )}
                />
              </div>
              <FieldControl
                name="issueDate"
                render={({ touched, hasError, pristine }) => (
                  <div className="inputContainer datepicker">
                    <DatePicker
                      id="issueDate"
                      className="form-control"
                      selected={this.state.issueDate}
                      onChange={this.handleIssueDateChange}
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      popperModifiers={{
                        flip: {
                          enabled: false,
                        },
                        preventOverflow: {
                          enabled: true,
                          escapeWithReference: false,
                        },
                      }}
                    />
                    <label
                      htmlFor="issueDate"
                      className={`label ${
                        pristine && !this.passportForm.controls.issueDate.value ? '' : 'notEmpty'
                      }`}
                    >
                      Passport Issue Date
                      <span className="error">
                        {(touched || submitted) && hasError('required') && ' - Required'}
                      </span>
                    </label>
                  </div>
                )}
              />
              <FieldControl
                name="expirationDate"
                render={({ touched, hasError, pristine }) => (
                  <div className="inputContainer datepicker">
                    <DatePicker
                      id="expirationDate"
                      className="form-control"
                      selected={this.state.expirationDate}
                      onChange={this.handleExpirationDateChange}
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      minDate={moment()}
                      popperModifiers={{
                        flip: {
                          enabled: false,
                        },
                        preventOverflow: {
                          enabled: true,
                          escapeWithReference: false,
                        },
                      }}
                    />
                    <label
                      htmlFor="expirationDate"
                      className={`label ${
                        pristine && !this.passportForm.controls.expirationDate.value
                          ? ''
                          : 'notEmpty'
                      }`}
                    >
                      Passport Expiration Date
                      <span className="error">
                        {(touched || submitted) &&
                          (hasError('required') || hasError('invalidExpirationDate')) &&
                          ' - Required'}
                      </span>
                    </label>
                  </div>
                )}
              />
              <p>
                <span className="round-icon small">2</span> Please upload a color photo of the photo
                page of your passport. Please be sure that all numbers and text are clearly visible
                and easy to read.
              </p>
              {this.state.passportDoc && !this.state.passportDoc.travelDocumentURL ? (
                <div>
                  <FieldControl
                    name="travelDocument"
                    render={({ touched, hasError }) => (
                      <div className="inputContainer">
                        <input
                          id="initial-pp-upload-field"
                          className="form-control"
                          type="file"
                          onChange={this.onImageSelect}
                        />
                        <label htmlFor="travelDocument" className="label notEmpty">
                          Passport Photo
                          <span className="error">
                            {(touched || submitted) && hasError('required') && ' - Required'}
                          </span>
                        </label>
                      </div>
                    )}
                  />
                </div>
              ) : null}
            </form>
          )}
        />
        <div>
          <p>
            Click{' '}
            <button className="btn-plain-text" onClick={this.props.openModal}>
              here
            </button>
            to see example picture of your passport.
          </p>
        </div>
        {this.state.passportDoc && this.state.passportDoc.travelDocumentURL ? (
          <div>
            <p>
              Passport Photo Uploaded!{' '}
              <button
                className="inline btn-plain-text"
                onClick={() => this.openModal('passportPreviewModal')}
              >
                &#8226; Preview
              </button>
              <span className="upload-btn-wrapper">
                &#8226; {!this.state.passport ? 'Update Photo' : 'Update'}
                <input id="update-pp-upload-field" type="file" onChange={this.onImageSelect} />
              </span>
            </p>
            <br />
          </div>
        ) : null}
        {this.state.message ? (
          <p
            id="error"
            className={`center ${this.state.successOrErrorMessage === true ? 'success' : 'error'}`}
          >
            {this.state.message}
          </p>
        ) : (
          ''
        )}
        <button
          onClick={e => this.handleSubmit(e)}
          className={`${this.state.loadingBtn ? 'btn-text-hide' : ''}`}
          type="submit"
          disabled={this.state.loadingBtn}
        >
          Submit
          <span className={`${this.state.loadingBtn ? 'loader' : ''}`} />
        </button>
        <br />
        <Modal
          isOpen={this.state.activeModal === 'passportPreviewModal'}
          onRequestClose={this.closeModal}
          overlayClassName="overlay"
        >
          <PassportPreviewModal previewUrl={this.state.previewUrl} closeModal={this.closeModal} />
        </Modal>
      </div>
    );
  }
}

Passport.propTypes = {
  setHeightState: PropTypes.func.isRequired,
  userId: PropTypes.string.isRequired,
  tripId: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  mailingCountry: PropTypes.string.isRequired,
  openModal: PropTypes.func.isRequired,
  destination: PropTypes.string.isRequired,
  setDocumentsAccordionState: PropTypes.func.isRequired,
  passport: PropTypes.shape({
    issueDate: PropTypes.string,
    expirationDate: PropTypes.string,
    id: PropTypes.string,
    passport: PropTypes.string,
    travelDocumentURL: PropTypes.string,
  }).isRequired,
};
